import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type {
  DtoIdentitiesLiabilityIdentifier,
  DtoV1UnderwritingSummariesLiabilityLiabilitySummaryResponse,
  DtoV1UnderwritingSummariesLiabilitySaveLiabilityRequest,
} from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { formatName } from '../common/utils/utils';
import { useLiability1003Store } from './liability1003Store';

const getLiabilities1003QueryKey = (
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
) => ['liabilities1003', obfuscatedLoanIdentifier] as const;

function transformLiabilityData(
  data: DtoV1UnderwritingSummariesLiabilityLiabilitySummaryResponse,
) {
  const liabilities = data.liabilities ?? [];

  return {
    ...data,
    liabilities,
    availableBorrowers: data.availableBorrowers ?? [],
    dropdownOptions: data.dropdownOptions ?? {},
    vaSummaryData: data.vaSummaryData ?? null,
    hasWritePermission: data.hasWritePermission ?? false,
    totalBalanceAndPayment: {
      payments: liabilities.reduce((sum, obj) => sum + (obj.payment ?? 0), 0),
      balance: liabilities.reduce((sum, obj) => sum + (obj.balance ?? 0), 0),
    },
  };
}

export type LiabilityData = ReturnType<typeof transformLiabilityData>;
export type LiabilityRecord = LiabilityData['liabilities'][number];

export function use1003LiabilitiesData<TSelected = LiabilityData>({
  select,
}: {
  select?: (data: LiabilityData) => TSelected;
} = {}) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: getLiabilities1003QueryKey(obfuscatedLoanIdentifier),
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.underwritingSummaryLiabilitiesGetLiabilitiesSummaryData(
          {
            obfuscatedLoanIdentifier,
            dropdownOptionsNeeded: true,
          },
        );

      return transformLiabilityData(data);
    },
    select,
  });
}

export type LiabilityFormData = ReturnType<
  typeof use1003LiabilitiesFormData
>['data']['formData'];

export function use1003LiabilitiesFormData(selectedLiabilityId: number) {
  const select = useCallback(
    (data: LiabilityData) => {
      const liability = data.liabilities?.find(
        (liability) =>
          liability.liabilityIdentifier.debtId === selectedLiabilityId,
      );

      return {
        formData: {
          ...liability,
          customerIdentifiers: liability?.customerIdentifiers?.map((id) =>
            String(id.borrowerRecordId),
          ),
          associatedRealEstate: liability?.addressIdentifier
            ? liability?.addressIdentifier?.addressId
            : '',
        },
        borrowerOptions: data?.availableBorrowers?.map((borrower) => ({
          label: formatName(
            borrower.firstName,
            borrower.lastName,
            borrower.middleName,
            borrower.suffixName,
          ),
          value: String(borrower.customerRecordId),
        })),
        dropdownOptions: data?.dropdownOptions,
        hasWritePermission: data?.hasWritePermission,
        vaSummaryData: data.vaSummaryData,
      };
    },
    [selectedLiabilityId],
  );

  return use1003LiabilitiesData<ReturnType<typeof select>>({
    select,
  });
}

type DeleteLiabilityPayload = {
  liabilityIdentifier?: DtoIdentitiesLiabilityIdentifier | null;
}[];

export function useDeleteLiabilitiesMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (rows: DeleteLiabilityPayload) => {
      const liabilitiesToDelete = rows.flatMap((row) =>
        row.liabilityIdentifier ? [{ ...row.liabilityIdentifier }] : [],
      );

      if (liabilitiesToDelete.length === 0) {
        return;
      }

      await typedApiClient.v1.underwritingSummaryLiabilitiesDeleteLiabilities({
        obfuscatedLoanIdentifier,
        liabilitiesToDelete,
      });
    },
    onSuccess: async (_data, rows) => {
      await queryClient.invalidateQueries({
        queryKey: getLiabilities1003QueryKey(obfuscatedLoanIdentifier),
      });

      useLiability1003Store.setState({ selectedLiabilityId: null });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        severity: 'success',
        message: rows.length > 1 ? 'Liabilities Deleted' : 'Liability Deleted',
        duration: 5000,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Records were not able to be deleted.',
        duration: 5000,
      });
    },
  });
}

export function useSaveLiabilityMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (
      payload: DtoV1UnderwritingSummariesLiabilitySaveLiabilityRequest,
    ) => {
      await typedApiClient.v1.underwritingSummaryLiabilitiesUpsertLiability({
        ...payload,
        obfuscatedLoanIdentifier,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getLiabilities1003QueryKey(obfuscatedLoanIdentifier),
      });

      useLiability1003Store.setState({ selectedLiabilityId: null });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        severity: 'success',
        message: 'Saved Successfully',
        duration: 5000,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Record was not able to be saved.',
        duration: 5000,
      });
    },
  });
}
